if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.onBreakpointChangeBound = this.onBreakpointChange.bind(this);
        this.addedMobileOverflowLock = false;
        this.header = document.querySelector('.header-wrapper');
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          overlay: this.querySelector('.country-selector__overlay'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('[aria-live="polite"]'),
        };
        if (this.elements.input) this.elements.input.disabled = false;
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        this.elements.button.addEventListener('click', this.openSelector.bind(this));

        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', () => this.hidePanel({ restoreFocus: true }));
        }
        if (this.elements.overlay) {
          this.elements.overlay.addEventListener('click', () => this.hidePanel({ restoreFocus: true }));
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

        this.querySelectorAll('a').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
      }

      connectedCallback() {
        if (this.breakpointListenerConnected) return;
        if (this.mql.addEventListener) this.mql.addEventListener('change', this.onBreakpointChangeBound);
        else this.mql.addListener?.(this.onBreakpointChangeBound);
        this.breakpointListenerConnected = true;
      }

      disconnectedCallback() {
        if (this.mql.removeEventListener) this.mql.removeEventListener('change', this.onBreakpointChangeBound);
        else this.mql.removeListener?.(this.onBreakpointChangeBound);
        this.breakpointListenerConnected = false;
        if (!this.elements.panel.hasAttribute('hidden')) this.hidePanel();
      }

      onBreakpointChange() {
        if (!this.elements.panel.hasAttribute('hidden')) this.hidePanel({ restoreFocus: true });
      }

      hidePanel({ restoreFocus = false } = {}) {
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
        this.elements.panel.removeAttribute('role');
        this.elements.panel.removeAttribute('aria-modal');
        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
        }
        if (this.addedMobileOverflowLock) releaseBodyScrollLock(this, 'overflow-hidden-mobile');
        this.addedMobileOverflowLock = false;
        document.querySelector('.menu-drawer')?.classList.remove('country-selector-open');
        if (this.header) this.header.preventHide = false;

        if (this.mobileFocusTrapped) {
          this.mobileFocusTrapped = false;
          const parentDrawer = this.closest('header-drawer');
          if (parentDrawer?.isConnected
            && parentDrawer.mainDetailsToggle?.isConnected
            && parentDrawer.mainDetailsToggle.hasAttribute('open')) {
            trapFocus(parentDrawer.mainDetailsToggle, this.elements.button);
          } else {
            removeTrapFocus(restoreFocus && this.elements.button.isConnected ? this.elements.button : null);
          }
        } else if (restoreFocus && this.elements.button.isConnected) {
          this.elements.button.focus();
        }
      }

      onContainerKeyDown(event) {
        const focusableItems = Array.from(this.querySelectorAll('a')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );
        if (focusableItems.length === 0) return;

        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus?.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus?.focus();
            break;
        }
      }

      onContainerKeyUp(event) {
        event.preventDefault();

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (this.elements.button.getAttribute('aria-expanded') == 'false') return;
            this.hidePanel({ restoreFocus: true });
            event.stopPropagation();
            break;
          case 'SPACE':
            if (this.elements.button.getAttribute('aria-expanded') == 'true') return;
            this.openSelector();
            break;
        }
      }

      onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        this.elements.input.value = event.currentTarget.dataset.value;
        if (form) form.submit();
      }

      openSelector() {
        if (!this.elements.panel.hasAttribute('hidden')) {
          this.hidePanel({ restoreFocus: true });
          return;
        }

        this.elements.button.focus();
        this.elements.panel.removeAttribute('hidden');
        this.elements.button.setAttribute('aria-expanded', 'true');
        this.addedMobileOverflowLock = false;
        if (!document.body.classList.contains('overflow-hidden-tablet')
          && !document.body.classList.contains('overflow-hidden-mobile')) {
          acquireBodyScrollLock(this, 'overflow-hidden-mobile');
          this.addedMobileOverflowLock = true;
        }
        if (this.elements.search && this.mql.matches) {
          this.elements.search.focus();
        }
        if (!this.mql.matches && this.elements.overlay) {
          this.elements.panel.setAttribute('role', 'dialog');
          this.elements.panel.setAttribute('aria-modal', 'true');
          const initialFocus = this.elements.search || this.elements.closeButton || this.querySelector('a');
          if (initialFocus) {
            trapFocus(this.elements.panel, initialFocus);
            this.mobileFocusTrapped = true;
          }
        }
        if (this.hasAttribute('data-prevent-hide')) {
          this.header.preventHide = true;
        }
        document.querySelector('.menu-drawer')?.classList.add('country-selector-open');
      }

      closeSelector(event) {
        if (
          event.target.classList.contains('country-selector__overlay') ||
          !this.contains(event.target) ||
          !this.contains(event.relatedTarget)
        ) {
          this.hidePanel();
        }
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.querySelector('.popular-countries');
        const allCountries = this.querySelectorAll('.countries a');
        let visibleCountries = 0;

        this.elements.resetButton.classList.toggle('hidden', !searchValue);

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', searchValue);
        }

        allCountries.forEach((item) => {
          const countryName = this.normalizeString(item.querySelector('.country').textContent);
          if (countryName.indexOf(searchValue) > -1) {
            item.parentElement.classList.remove('hidden');
            visibleCountries++;
          } else {
            item.parentElement.classList.add('hidden');
          }
        });

        if (this.elements.liveRegion) {
          this.elements.liveRegion.textContent = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountries
          );
        }

        this.querySelector('.country-selector').scrollTop = 0;
        this.querySelector('.country-selector__list').scrollTop = 0;
      }

      resetFilter(event) {
        event.stopPropagation();
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search.value) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}
